import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import {
  BsSnow,
  BsCloudLightningRain,
  BsCloudRainHeavy,
  BsWind,
  BsCloudFog,
  BsThermometerSnow,
  BsThermometerHigh,
  BsThermometerLow,
  BsExclamationTriangle,
  BsShieldExclamation,
  BsGeoAltFill,
  BsLightbulbFill,
  BsArrowRight,
  BsCarFrontFill,
} from 'react-icons/bs';
import {
  WarningTypes,
  WarningSeverity,
  severityColors,
} from '../../../../config/weatherWarnings';
import {
  getWarningMessage,
  hasUrgentWarnings,
  getWarningsSummaryText,
} from '../../../../util/WeatherWarnings';

// Icon mapping
const iconMap = {
  [WarningTypes.SNOW]: BsSnow,
  [WarningTypes.STORM]: BsCloudLightningRain,
  [WarningTypes.HEAVY_RAIN]: BsCloudRainHeavy,
  [WarningTypes.STRONG_WIND]: BsWind,
  [WarningTypes.FOG]: BsCloudFog,
  [WarningTypes.ICE]: BsThermometerSnow,
  [WarningTypes.EXTREME_HEAT]: BsThermometerHigh,
  [WarningTypes.EXTREME_COLD]: BsThermometerLow,
};

// Severity badge text
const severityBadgeText = {
  [WarningSeverity.INFO]: 'Información',
  [WarningSeverity.WARNING]: 'Precaución',
  [WarningSeverity.DANGER]: 'Peligro',
  [WarningSeverity.EXTREME]: 'Extremo',
};

// Severity badge color scheme
const severityBadgeColor = {
  [WarningSeverity.INFO]: 'blue',
  [WarningSeverity.WARNING]: 'yellow',
  [WarningSeverity.DANGER]: 'orange',
  [WarningSeverity.EXTREME]: 'red',
};

function WarningItem({ warning }) {
  const { title, message } = getWarningMessage(warning);
  const WarningIcon = iconMap[warning.type] || BsExclamationTriangle;
  const colors = severityColors[warning.severity];

  return (
    <Box
      w="100%"
      p={4}
      borderRadius="md"
      bg={colors.bg}
      borderLeft="4px solid"
      borderLeftColor={colors.border}
      mb={3}
    >
      <HStack spacing={3} align="flex-start">
        <Icon as={WarningIcon} boxSize={6} color={colors.icon} mt={1} />
        <VStack align="flex-start" spacing={1} flex={1}>
          <HStack justify="space-between" w="100%">
            <Text fontWeight="bold" color={colors.text}>
              {title}
            </Text>
            <Badge colorScheme={severityBadgeColor[warning.severity]}>
              {severityBadgeText[warning.severity]}
            </Badge>
          </HStack>
          <Text fontSize="sm" color={colors.text}>
            {message}
          </Text>
          {warning.localityName && (
            <HStack spacing={1} mt={1}>
              <Icon as={BsGeoAltFill} boxSize={3} color="gray.500" />
              <Text fontSize="xs" color="gray.600">
                {warning.localityName}
                {warning.localityProvince
                  ? `, ${warning.localityProvince}`
                  : ''}
              </Text>
            </HStack>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}

function WarningsByLocality({ warningsByLocality }) {
  const localities = Object.keys(warningsByLocality);

  if (localities.length === 0) return null;

  return (
    <Accordion allowMultiple mt={4}>
      {localities.map((localityName, index) => {
        const localityWarnings = warningsByLocality[localityName];
        const uniqueTypes = [...new Set(localityWarnings.map(w => w.type))];
        const maxSeverity = localityWarnings.reduce((max, w) => {
          const severityOrder = { extreme: 4, danger: 3, warning: 2, info: 1 };
          return severityOrder[w.severity] > severityOrder[max]
            ? w.severity
            : max;
        }, 'info');

        return (
          <AccordionItem key={index}>
            <h2>
              <AccordionButton>
                <HStack flex="1" textAlign="left" spacing={2}>
                  <Icon
                    as={BsShieldExclamation}
                    color={severityColors[maxSeverity].icon}
                  />
                  <Text fontWeight="medium">{localityName}</Text>
                  <Badge
                    colorScheme={severityBadgeColor[maxSeverity]}
                    size="sm"
                  >
                    {uniqueTypes.length} alerta
                    {uniqueTypes.length > 1 ? 's' : ''}
                  </Badge>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <VStack spacing={2} align="stretch">
                {uniqueTypes.map((type, idx) => {
                  const warning = localityWarnings.find(w => w.type === type);
                  return <WarningItem key={idx} warning={warning} />;
                })}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default function WeatherWarningsModal({
  isOpen,
  onClose,
  warningsAnalysis,
  origin,
  destination,
}) {
  if (!warningsAnalysis || !warningsAnalysis.hasWarnings) {
    return null;
  }

  const { warnings, warningsByLocality } = warningsAnalysis;
  const isUrgent = hasUrgentWarnings(warningsAnalysis);
  const summaryText = getWarningsSummaryText(warningsAnalysis);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
      <ModalContent mx={4}>
        <ModalHeader pb={2}>
          <VStack align="start" spacing={1}>
            <HStack spacing={2}>
              <Icon
                as={BsShieldExclamation}
                color={isUrgent ? 'red.500' : 'orange.500'}
                boxSize={6}
              />
              <Text>Alertas Meteorológicas</Text>
            </HStack>
            {(origin || destination) && (
              <HStack spacing={2} color="gray.700">
                <Icon as={BsCarFrontFill} boxSize={4} color="blue.500" />
                <Text fontSize="md" fontWeight="medium">
                  {origin || 'Origen'}
                </Text>
                <Icon as={BsArrowRight} boxSize={3} />
                <Text fontSize="md" fontWeight="medium">
                  {destination || 'Destino'}
                </Text>
              </HStack>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {/* Summary Alert */}
          <Alert
            status={isUrgent ? 'error' : 'warning'}
            borderRadius="md"
            mb={4}
          >
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">
                {isUrgent ? '¡Atención!' : 'Aviso'}
              </AlertTitle>
              <AlertDescription fontSize="sm">{summaryText}</AlertDescription>
            </Box>
          </Alert>

          {/* Main Warnings */}
          <Text fontWeight="semibold" mb={3} color="gray.700">
            Resumen de alertas
          </Text>
          <VStack spacing={0} align="stretch">
            {warnings.map((warning, index) => (
              <WarningItem key={index} warning={warning} />
            ))}
          </VStack>

          {/* Warnings by Locality */}
          {Object.keys(warningsByLocality).length > 1 && (
            <>
              <Divider my={4} />
              <Text fontWeight="semibold" mb={2} color="gray.700">
                Alertas por localidad
              </Text>
              <WarningsByLocality warningsByLocality={warningsByLocality} />
            </>
          )}

          {/* Safety Tips */}
          <Box mt={4} p={4} bg="gray.50" borderRadius="md">
            <HStack spacing={2} mb={2}>
              <Icon as={BsLightbulbFill} boxSize={4} color="yellow.500" />
              <Text fontWeight="semibold" fontSize="sm">
                Consejos de seguridad
              </Text>
            </HStack>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xs" color="gray.600">
                • Consulta el estado de las carreteras antes de salir
              </Text>
              <Text fontSize="xs" color="gray.600">
                • Lleva un kit de emergencia en el vehículo
              </Text>
              <Text fontSize="xs" color="gray.600">
                • Informa a alguien de tu ruta y hora estimada de llegada
              </Text>
              <Text fontSize="xs" color="gray.600">
                • En caso de duda, considera retrasar el viaje
              </Text>
            </VStack>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Entendido
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
